import InfoIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin';

import { BOResourceAll } from '@Common/types/bo_external/resources';

import { useCanAccess } from '@sh4res/bo-common/hooks';

type LastOtpRecord = {
  id: number;
  has_last_otp: boolean;
};

type LastOtpFieldProps = {
  resourcePath: BOResourceAll;
  labels: {
    show: string;
    hide: string;
    noOtp: string;
    unavailable: string;
    fetchError: string;
  };
  'data-testid'?: string;
};

type LastOtpResponse = {
  id: number;
  code: string | null;
};

export function LastOtpField({
  resourcePath,
  labels,
  'data-testid': dataTestId,
}: LastOtpFieldProps) {
  const record = useRecordContext<LastOtpRecord>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const canShowLastOtp = useCanAccess({
    resource: resourcePath,
    action: 'show',
  });
  const [otp, setOtp] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const canFetchOtp = Boolean(record?.has_last_otp) && canShowLastOtp;

  const otpDisplayValue = useMemo(() => {
    if (!canFetchOtp) {
      return '-';
    }

    return isVisible && otp ? otp : '******';
  }, [canFetchOtp, isVisible, otp]);

  useEffect(() => {
    setOtp(null);
    setIsVisible(false);
  }, [record?.id, record?.has_last_otp]);

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      data-testid={dataTestId ?? 'BoLastOtpField'}>
      <Typography>{otpDisplayValue}</Typography>
      {canFetchOtp ? (
        <Tooltip title={translate(isVisible ? labels.hide : labels.show)}>
          <span>
            <IconButton
              data-testid="BoExternalLastOtpField_ToggleVisibilityButton"
              size="small"
              color="primary"
              disabled={loading}
              onClick={async () => {
                if (!record) {
                  return;
                }

                if (isVisible) {
                  setIsVisible(false);
                  return;
                }

                setLoading(true);
                try {
                  const { data } = await dataProvider.getOne<LastOtpResponse>(resourcePath, {
                    id: record.id,
                  });

                  if (!data.code) {
                    setOtp(null);
                    setIsVisible(false);
                    refresh();
                    notify(labels.unavailable, {
                      type: 'warning',
                    });
                    return;
                  }

                  setOtp(data.code);
                  setIsVisible(true);
                  refresh();
                } catch {
                  setOtp(null);
                  setIsVisible(false);
                  notify(labels.fetchError, {
                    type: 'error',
                  });
                } finally {
                  setLoading(false);
                }
              }}>
              {isVisible ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={translate(labels.noOtp)}>
          <InfoIcon color="primary" fontSize="small" />
        </Tooltip>
      )}
    </Stack>
  );
}
